import type { TableQueryPayload } from '~/types/demo'
import type { RouteRecordRaw } from "#vue-router";

export const HOME_ROOT = '/' as const

export const PAGE_SIZE = 30

export default function makeLangRoutes (routesIn: RouteRecordRaw[], lang: string, isChild = false) {
  const routes: RouteRecordRaw[] = []
  routesIn.forEach((r) => {
    let path = r.path
    let children = r.children
    let alias = r.alias ?? []
    // Only change the path of top level (root) routes
    if (!isChild && path) {
      // use regex to limit sublocale lang path see https://github.com/pillarjs/path-to-regexp/tree/v1.7.0#parameters
      path = `/${lang}${path === '/' ? '' : path}`
      if (alias) {
        alias = Array.isArray(alias) ? alias : [alias];
        alias = alias.map(v => {
          return `/${lang}${v === '/' ? '' : v}`
        })
      } else {
        alias = []
      }
    }

    // Handle child routes (recursive)
    children = children ? makeLangRoutes(children, lang, true) : children
    // Build a new route object — spread over a union member; cast to preserve RouteRecordRaw contract.
    // nuxt2 also renamed these routes to `lang-<name>`. Names stay unchanged here
    // because navigation goes by name, so `langPrefixed` marks them instead, for the
    // GTM page view, which still reports nuxt2's name. AB#64563
    const route = { ...r, alias: alias as string | string[], path, children, meta: { ...r.meta, langPrefixed: true } } as RouteRecordRaw
    // Append the new route
    routes.push(route)
  })
  // Return the new array of routes
  return routes
}

// Copied from https://github.com/vuejs/vue-router/blob/dev/src/util/query.js
const encodeReserveRE = /[!'()*]/g
const encodeReserveReplacer = (c: string) => '%' + c.charCodeAt(0).toString(16)
const commaRE = /%2C/g
const encode = (str: string) => encodeURIComponent(str)
  .replace(encodeReserveRE, encodeReserveReplacer)
  .replace(commaRE, ',')

export function stringifyQuery (obj: Record<string, string | undefined>): string {
  const res = obj
    ? Object.keys(obj).map((key) => {
      const val = obj[key]

      if (val === undefined) {
        return ''
      }

      if (val === null) {
        return encode(key)
      }

      if (Array.isArray(val)) {
        const result: string[] = []
        val.forEach((val2) => {
          if (val2 === undefined) {
            return
          }
          if (val2 === null) {
            result.push(encode(key))
          } else {
            result.push(encode(key) + '=' + encode(val2))
          }
        })
        return result.join('&')
      }

      return encode(key) + '=' + encode(val)
    }).filter(x => x.length > 0).join('&')
    : null
  return res ? `?${res}` : ''
}

export const filterTableUrlParams = (query: TableQueryPayload) => {
  // converts pages param in url that is due to seo compat to page param from api.
  // filters url params to remove null and undefined params
  const replacedParams = Object.assign({}, query, { page: query.pages, pages: undefined, pageSize: query.pageSize ?? PAGE_SIZE })
  return Object.fromEntries(
    Object.entries(replacedParams).filter(([_, v]) => v !== null && v !== undefined)
  )
}

type Query = { [key: string]: string | (string | null)[] }
export const processQueryFilter = (query: Query) => {
  const filters = { ...query }
  for (const dateFieldName of ['purchaseDate', 'orderDate', 'orderTotal', 'signUpDate']) {
    if (filters[dateFieldName]) {
      const date = new Date()
      const getDateStringNoTimezoneOffset = (date: Date) => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString()
      switch (filters[dateFieldName]) {
        case 'this':
          filters[dateFieldName + 'Begin'] = getDateStringNoTimezoneOffset(new Date(date.getFullYear(), date.getMonth(), 1))
          break
        case 'last':
          filters[dateFieldName + 'Begin'] = getDateStringNoTimezoneOffset(new Date(date.getFullYear(), date.getMonth() - 1, 1))
          filters[dateFieldName + 'End'] = getDateStringNoTimezoneOffset(new Date(date.getFullYear(), date.getMonth(), 0))
          break
        case 'last30':
          filters[dateFieldName + 'Begin'] = getDateStringNoTimezoneOffset(new Date(new Date().setDate(date.getDate() - 30)))
          break
        case 'last60':
          filters[dateFieldName + 'Begin'] = getDateStringNoTimezoneOffset(new Date(new Date().setDate(date.getDate() - 60)))
          break
        case 'last90':
          filters[dateFieldName + 'Begin'] = getDateStringNoTimezoneOffset(new Date(new Date().setDate(date.getDate() - 90)))
          break
        default: {
          if (filters[dateFieldName]?.length === 2) {
            const [begin, end] = [...filters[dateFieldName]]
            filters[dateFieldName + 'Begin'] = begin as string
            filters[dateFieldName + 'End'] = end as string
          }
        }
      }
      if (!filters[dateFieldName + 'End'] && !filters.orderTotal) {
        filters[dateFieldName + 'End'] = getDateStringNoTimezoneOffset(new Date(date.getFullYear(), date.getMonth(), date.getDate()))
      }
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- removing optional field by computed key
      delete filters[dateFieldName]
    }
  }
  for (const dateFieldName of ['csv', 'purchaseAmount', 'orderTotal']) {
    const rangeValue = filters[dateFieldName + 'Range']
    if (Array.isArray(rangeValue)) {
      filters[dateFieldName + 'Begin'] = rangeValue[0] as string
      filters[dateFieldName + 'End'] = rangeValue[1] as string
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- removing optional field by computed key
      delete filters[dateFieldName + 'Range']
    }
  }

  return filters
}

// The home route, plain or localized. Shared so a new home route name is one edit.
export const isHomepage = (name: string | symbol | undefined) => checkRouteName(name, ['index', 'lang-index'])

export const checkRouteName = (route: string | symbol | undefined, name: string | readonly string[])  => {
  if (typeof route === 'string') {
    if (typeof name === 'object') {
      return name.includes(route);
    } else {
      return route.includes(name);
    }
  } else if (route) {
    const key = Symbol.keyFor(route)
    if (!key) return false
    if (typeof name === 'string') {
      return key.includes(name)
    }
    return name.some(n => key.includes(n));
  } else {
    return false
  }
}